import React, { useEffect } from 'react';
import { X } from 'lucide-react';
import AssistantConversation from '../assistant/AssistantConversation';
import ConversationHistoryMenu from '../assistant/ConversationHistoryMenu';

interface QuickStartModalProps {
  isOpen: boolean;
  onClose: () => void;
}

/**
 * Hosts Arie as a centered modal instead of the corner chat bubble — same
 * thread (QuickStartAssistantContext), same conversation, just a different
 * surface. The floating chat bubble (SupportChatWindow) renders the same
 * AssistantConversation component against the same thread.
 */
const QuickStartModal: React.FC<QuickStartModalProps> = ({ isOpen, onClose }) => {
  useEffect(() => {
    if (!isOpen) return;
    const handler = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };
    window.addEventListener('keydown', handler);
    return () => window.removeEventListener('keydown', handler);
  }, [isOpen, onClose]);

  if (!isOpen) return null;

  return (
    <div
      className="fixed inset-0 z-[70] bg-black/50 flex items-center justify-center p-4"
      role="dialog"
      aria-modal="true"
      aria-labelledby="quickstart-modal-title"
      onClick={onClose}
    >
      <div
        className="w-full max-w-2xl h-[80vh] flex flex-col bg-white dark:bg-gray-800 rounded-xl shadow-xl overflow-hidden"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-center justify-between px-6 pt-5 pb-1">
          <h2 id="quickstart-modal-title" className="text-base font-semibold text-gray-900 dark:text-gray-100">
            Arie
          </h2>
          <div className="flex items-center gap-1">
            <ConversationHistoryMenu theme="light" />
            <button
              onClick={onClose}
              aria-label="Close Arie"
              className="p-1.5 rounded-lg text-gray-400 hover:text-gray-600 dark:hover:text-gray-200 hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors"
            >
              <X className="w-5 h-5" />
            </button>
          </div>
        </div>
        <div className="flex-1 min-h-0">
          <AssistantConversation variant="modal" onNavigated={onClose} />
        </div>
      </div>
    </div>
  );
};

export default QuickStartModal;
